import { apiClient } from '@/services/api'
import { appendSiteParam } from '@/utils/sceneUtils'

export function getVizrtThumbnailUrl(templateId) {
    if (!templateId) return null
    const base = (apiClient.defaults.baseURL || '').replace(/\/+$/, '')
    return appendSiteParam(`${base}/globographics/vizrt/templates/${encodeURIComponent(templateId)}/thumbnail`)
}

const state = () => ({
    folders: [],
    files: [],
    breadcrumb: [],
    searchResults: null,
    savedScenes: [],
    linkedProjects: [],
    loading: false,
    vizrtTemplateModel: null,
    vizrtHealth: { graphicHub: { healthy: false }, pds: { healthy: false }, preview: { healthy: false }, mse: { healthy: false } },
    // false until the first probe resolves, so the offline screen never flashes on
    // mount from the all-false default before Vizrt has actually been reached.
    vizrtHealthChecked: false,
    productionImages: [],
    productionImagesLoading: false,
    lastError: null,
})

const getters = {
    getterFolders: state => state.folders,
    getterFiles: state => state.files,
    getterBreadcrumb: state => state.breadcrumb,
    getterSearchResults: state => state.searchResults,
    getterSavedScenes: state => state.savedScenes,
    getterLinkedProjects: state => state.linkedProjects,
    getterLoading: state => state.loading,
    getterVizrtHealth: state => state.vizrtHealth,
    getterVizrtHealthChecked: state => state.vizrtHealthChecked,
    getterProductionImages: state => state.productionImages,
    getterProductionImagesLoading: state => state.productionImagesLoading,
}

const actions = {
    async getRootFolders({ commit }) {
        commit('setLastError', null)
        commit('setLoading', true)
        try {
            const { data } = await apiClient.get('globographics/folders')
            commit('setFolders', data)
            commit('setBreadcrumb', [{ uuid: null, title: 'Root' }])
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao carregar pastas do Graphic Hub'
            commit('setLastError', msg)
        } finally {
            commit('setLoading', false)
        }
    },

    async navigateToFolder({ commit, state, dispatch }, { uuid, title }) {
        commit('setLastError', null)
        commit('setLoading', true)
        try {
            const { data } = await apiClient.get(`globographics/folders/${uuid}`)
            commit('setFolders', data)

            const breadcrumb = [...state.breadcrumb]
            const existingIndex = breadcrumb.findIndex(b => b.uuid === uuid)
            if (existingIndex >= 0) {
                commit('setBreadcrumb', breadcrumb.slice(0, existingIndex + 1))
            } else {
                breadcrumb.push({ uuid, title })
                commit('setBreadcrumb', breadcrumb)
            }

            // Auto-load files for the current folder
            await dispatch('loadFolderFiles', { uuid })
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao navegar na pasta'
            commit('setLastError', msg)
        } finally {
            commit('setLoading', false)
        }
    },

    async loadFolderFiles({ commit }, { uuid, term }) {
        commit('setLastError', null)
        commit('setLoading', true)
        try {
            let url = `globographics/folders/${uuid}/files`
            if (term) {
                const params = new URLSearchParams({ term })
                url += `?${params}`
            }
            const { data } = await apiClient.get(url)
            commit('setFiles', data)
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao carregar arquivos'
            commit('setLastError', msg)
        } finally {
            commit('setLoading', false)
        }
    },

    async searchFiles({ commit }, { q, category, count, startIndex }) {
        commit('setLastError', null)
        commit('setSearchResults', null)
        commit('setLoading', true)
        try {
            const params = new URLSearchParams({ q })
            if (category) params.append('category', category)
            if (count) params.append('count', count)
            if (startIndex != null) params.append('startIndex', startIndex)
            const { data } = await apiClient.get(`globographics/search?${params}`)
            commit('setSearchResults', data)
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao pesquisar'
            commit('setLastError', msg)
        } finally {
            commit('setLoading', false)
        }
    },

    clearSearch({ commit }) {
        commit('setSearchResults', null)
    },

    async loadLinkedProjects({ commit }, { codProducao }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.get(`globographics/projetos/${codProducao}`)
            commit('setLinkedProjects', data)
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao carregar projetos vinculados'
            commit('setLastError', msg)
            // Clear on failure. These slots are production-scoped and this action is
            // dispatched on every production switch, so leaving the previous value in
            // place renders one production's data under another's heading.
            commit('setLinkedProjects', [])
        }
    },

    async linkProject({ commit, dispatch }, { cod_producao, dsc_uuid_projeto_gh, dsc_nome_projeto_gh, dsc_caminho_gh }) {
        commit('setLastError', null)
        try {
            await apiClient.post('globographics/projetos', {
                cod_producao,
                dsc_uuid_projeto_gh,
                dsc_nome_projeto_gh,
                dsc_caminho_gh,
            })
            dispatch('loadLinkedProjects', { codProducao: cod_producao })
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao vincular projeto'
            commit('setLastError', msg)
            throw error
        }
    },

    async unlinkProject({ commit, dispatch }, { idGhProjeto, codProducao }) {
        commit('setLastError', null)
        try {
            await apiClient.delete(`globographics/projetos/${idGhProjeto}`)
            dispatch('loadLinkedProjects', { codProducao })
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao desvincular projeto'
            commit('setLastError', msg)
            throw error
        }
    },

    async loadSavedScenes({ commit }, { codProducao }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.get(`globographics/cenas/${codProducao}`)
            commit('setSavedScenes', data)
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao carregar cenas salvas'
            commit('setLastError', msg)
            // Clear on failure, not just flag the error. This slot also backs
            // playout's preview-item getter, so a stale list after a production
            // switch could seed the new production's playlist with the old
            // production's cena — which the API must then reject.
            commit('setSavedScenes', [])
        }
    },

    async deleteCena({ commit, dispatch }, { idGhCena, codProducao }) {
        commit('setLastError', null)
        try {
            await apiClient.delete(`globographics/cenas/${idGhCena}`)
            // Cena deletion cascades to playlist items server-side — refresh
            // both slices so the playlist panel doesn't show a ghost item.
            await Promise.all([
                dispatch('loadSavedScenes', { codProducao }),
                dispatch('playout/fetchPlaylists', codProducao, { root: true }).catch(() => {}),
            ])
        } catch (error) {
            // Don't overwrite lastError with a generic message — the 409
            // CENA_ON_AIR case is user-actionable and surfaced via the
            // caller's toast using error.response.data.error directly.
            if (error.response?.data?.code !== 'CENA_ON_AIR') {
                const msg = error.response?.data?.error || 'Erro ao excluir cena'
                commit('setLastError', msg)
            }
            throw error
        }
    },

    // `after` is a keyset cursor. Prefer it over `page`: the API materialises the
    // whole PDS catalog per pod, so offsets drift when consecutive pages are
    // served by different replicas and entries get silently skipped.
    async fetchVizrtTemplates({ commit }, { page, size, q, after } = {}) {
        commit('setLastError', null)
        try {
            const params = new URLSearchParams()
            if (after) params.append('after', after)
            else if (page) params.append('page', page)
            if (size) params.append('size', size)
            if (q) params.append('q', q)
            const { data } = await apiClient.get(`globographics/vizrt/templates?${params}`)
            // Deliberately NOT held in the store. Several drawers page this catalog
            // independently, so a shared slot meant one drawer rendering the page
            // another had fetched — the caller takes the return value instead.
            return data
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao carregar templates Vizrt'
            commit('setLastError', msg)
            // Rethrow so a failed page is visible to the caller rather than looking
            // like an empty catalog.
            throw error
        }
    },

    async fetchVizrtTemplateModel({ commit }, templateId) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.get(`globographics/vizrt/templates/${encodeURIComponent(templateId)}/model`)
            commit('setVizrtTemplateModel', data)
            return data
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao carregar modelo do template'
            commit('setLastError', msg)
        }
    },

    async saveVizrtCena({ commit, dispatch }, { templateId, fieldValues, cod_producao, idGhCena, dsc_nome_cena, tags }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.post('globographics/vizrt/save', {
                templateId,
                fieldValues,
                cod_producao,
                idGhCena,
                dsc_nome_cena,
                tags,
            })
            dispatch('loadSavedScenes', { codProducao: cod_producao })
            if (data?.msePropagation?.mseUpdated || data?.msePropagation?.liveUpdated) {
                // Cross-module fire-and-forget — explicit .catch so a stale
                // playlist refresh failure can't bubble as an unhandled
                // rejection (the save itself already succeeded).
                dispatch('playout/fetchPlaylists', cod_producao, { root: true }).catch(() => {})
            }
            // The save succeeded in the database and did NOT reach the Media Sequencer.
            // The API has always sent mseError for this and nothing ever read it, so the
            // operator was told "salvo" either way — including when the cena was ON AIR
            // and the picture on television therefore did not change. Surfaced as
            // lastError rather than thrown: the save itself really did work, and the row
            // in the list is correct; what failed is the part they cannot see.
            if (data?.mseError) {
                commit('setLastError', data.msePropagation?.mseUpdated
                    ? `Cena salva, mas não foi possível atualizar o que está no ar: ${data.mseError}`
                    : `Cena salva, mas não foi sincronizada com o Media Sequencer: ${data.mseError}`)
            }
            return data
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao salvar cena'
            commit('setLastError', msg)
            throw error
        }
    },

    // Bulk import for the multi-select catalog drawer. One round-trip and one
    // savedScenes refresh, versus a save + refresh pair per template. Returns
    // { created, failed } — partial success is normal and must be surfaced.
    async saveVizrtCenasBulk({ commit, dispatch }, { cod_producao, templates }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.post('globographics/vizrt/save/bulk', { cod_producao, templates })
            await dispatch('loadSavedScenes', { codProducao: cod_producao })
            return data
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao importar templates'
            commit('setLastError', msg)
            throw error
        }
    },

    async checkVizrtHealth({ commit }) {
        try {
            const { data } = await apiClient.get('globographics/vizrt/health')
            commit('setVizrtHealth', data)
        } catch (error) {
            commit('setVizrtHealth', { graphicHub: { healthy: false }, pds: { healthy: false }, preview: { healthy: false }, mse: { healthy: false } })
        }
    },

    async fetchProductionImages({ commit }, { codProducao }) {
        commit('setLastError', null)
        commit('setProductionImagesLoading', true)
        try {
            const { data } = await apiClient.get(`globographics/images/${codProducao}`)
            commit('setProductionImages', data)
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao carregar imagens da produção'
            commit('setLastError', msg)
            // Clear on failure: the image picker would otherwise offer the previous
            // production's uploads for insertion into this one's cena.
            commit('setProductionImages', [])
        } finally {
            commit('setProductionImagesLoading', false)
        }
    },

    async uploadProductionImage({ commit, dispatch }, { codProducao, file }) {
        commit('setLastError', null)
        try {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('cod_producao', codProducao)
            const { data } = await apiClient.post('globographics/images/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            await dispatch('fetchProductionImages', { codProducao })
            return data
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao fazer upload da imagem'
            commit('setLastError', msg)
            throw error
        }
    },

    async importGhImage({ commit, dispatch }, { codProducao, fileUuid, fileName, mimeType }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.post('globographics/images/import-gh', {
                cod_producao: codProducao,
                fileUuid,
                fileName,
                mimeType,
            })
            await dispatch('fetchProductionImages', { codProducao })
            return data
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao importar imagem do Graphic Hub'
            commit('setLastError', msg)
            throw error
        }
    },

    async deleteProductionImage({ commit, dispatch }, { idGhImagem, codProducao }) {
        commit('setLastError', null)
        try {
            await apiClient.delete(`globographics/images/${idGhImagem}`)
            await dispatch('fetchProductionImages', { codProducao })
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao excluir imagem'
            commit('setLastError', msg)
            throw error
        }
    },

}

const mutations = {
    setFolders(state, folders) { state.folders = folders },
    setFiles(state, files) { state.files = files },
    setBreadcrumb(state, breadcrumb) { state.breadcrumb = breadcrumb },
    setSearchResults(state, results) { state.searchResults = results },
    setSavedScenes(state, scenes) { state.savedScenes = scenes },
    // Move the on-air marker across the saved-cena chips without refetching the
    // list. A take changes exactly one thing about these rows — which of them is
    // live — so re-downloading every cena of the production to learn it put a
    // whole extra round-trip in front of the operator's button. Pass null to clear
    // (Takeout). Assigning to an existing property keeps Vue 2 reactivity.
    applyCenaOnAir(state, idGhCena) {
        for (const scene of state.savedScenes || []) {
            const onAir = idGhCena != null && scene.id_gh_cena === idGhCena
            if (scene.flg_no_ar !== onAir) scene.flg_no_ar = onAir
        }
    },
    setLinkedProjects(state, projects) { state.linkedProjects = projects },
    setLoading(state, loading) { state.loading = loading },
    setVizrtTemplateModel(state, model) { state.vizrtTemplateModel = model },
    setVizrtHealth(state, health) { state.vizrtHealth = health; state.vizrtHealthChecked = true },
    setProductionImages(state, images) { state.productionImages = images },
    setProductionImagesLoading(state, loading) { state.productionImagesLoading = loading },
    setLastError(state, error) { state.lastError = error },
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}
